<script setup lang="ts">
import type { EChartsOption } from 'echarts'
import { LazyVChart } from '~/components/ui/chart'
import type { IBankingBlockResponseMap, IBankingCurrencyPoint, IBankingFact, IBankingRecord } from '../types'
import { useBankingNumber } from '../useBankingNumber'
import WidgetBankingStatsBlock from '../WidgetBankingStatsBlock.vue'

type CurrencyLine = 'deposits' | 'credits'

const props = defineProps<{
  /** Facts of the summary: a record of the deposits share turns the headline into a record one. */
  facts: IBankingFact[]
}>()

const LINES: CurrencyLine[] = ['deposits', 'credits']
const LINE_FIELD: Record<CurrencyLine, keyof Omit<IBankingCurrencyPoint, 'period'>> = {
  deposits: 'deposits_fx_share',
  credits: 'credits_fx_share'
}
const LINE_COLORS: Record<CurrencyLine, string> = { deposits: '#2563eb', credits: '#d97706' }
const RECORD_SERIES = 'deposits_fx_share'
const YEAR_LABEL_STEP = 5

const { t } = useI18n()
const { formatNumber, formatMonth } = useBankingNumber()

const { data: response } = useApi<IBankingBlockResponseMap['currency']>('/banking-statistics/blocks/currency', {
  key: 'banking-statistics-currency',
  lazy: true
})

const points = computed<IBankingCurrencyPoint[]>(() => response.value?.data.points ?? [])
const records = computed(() => {
  const value = response.value?.data.records

  return value && !Array.isArray(value) ? value : null
})
const asOf = computed(() => points.value.at(-1)?.period ?? null)

const share = (value: number): string => `${formatNumber(value, 1)}%`
const year = (period: string): string => period.slice(0, 4)

const title = computed<{ key: string; params: Record<string, string | number> }>(() => {
  const current = points.value.findLast((point) => point.deposits_fx_share !== null)?.deposits_fx_share

  if (current === undefined || current === null) {
    return { key: 'banking_statistics.currency.title_neutral', params: {} }
  }

  // Records are found by the backend over all months; the page only repeats them.
  const record = props.facts.find(
    (fact) =>
      (fact.key === 'record_low' || fact.key === 'record_high') &&
      fact.params.series === RECORD_SERIES &&
      typeof fact.params.since_year === 'number'
  )

  if (record) {
    return {
      key: `banking_statistics.currency.title_${record.key}`,
      params: { value: share(current), since_year: Number(record.params.since_year) }
    }
  }

  const peak = records.value?.deposits.max

  if (!peak) {
    return { key: 'banking_statistics.currency.title_neutral', params: {} }
  }

  return {
    key: 'banking_statistics.currency.title',
    params: { value: share(current), peak: share(peak.value), peak_year: year(peak.period) }
  }
})

const isChartReady = ref(false)

onMounted(async () => {
  await import('./mark-point')
  isChartReady.value = true
})

const recordMark = (record: IBankingRecord, kind: 'max' | 'min', color: string) => ({
  name: t(`banking_statistics.currency.${kind}`),
  coord: [formatMonth(record.period), record.value],
  value: record.value,
  symbol: 'circle',
  symbolSize: 8,
  itemStyle: { color: '#ffffff', borderColor: color, borderWidth: 2 },
  label: {
    show: true,
    position: kind === 'max' ? ('top' as const) : ('right' as const),
    formatter: `${share(record.value)}, ${year(record.period)}`,
    color,
    fontSize: 11,
    fontWeight: 600 as const
  }
})

const chartOption = computed<EChartsOption>(() => ({
  grid: { left: 8, right: 16, top: 40, bottom: 8, containLabel: true },
  legend: { top: 0, left: 0, itemWidth: 16, textStyle: { fontSize: 12 } },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: points.value.map((point) => formatMonth(point.period)),
    axisTick: { show: false },
    axisLine: { lineStyle: { color: '#e5e7eb' } },
    // Labels "MM.YYYY": one per five years, on January.
    axisLabel: {
      fontSize: 11,
      color: '#6b7280',
      hideOverlap: true,
      interval: (_index: number, value: string) =>
        value.startsWith('01.') && Number(value.slice(3)) % YEAR_LABEL_STEP === 0,
      formatter: (value: string) => value.slice(3)
    }
  },
  yAxis: {
    type: 'value',
    min: 0,
    max: 100,
    axisLabel: { formatter: '{value}%' },
    splitLine: { lineStyle: { color: '#f3f4f6' } }
  },
  tooltip: {
    trigger: 'axis',
    valueFormatter: (value) => (typeof value === 'number' ? share(value) : '—')
  },
  series: LINES.map((line) => {
    const color = LINE_COLORS[line]
    const lineRecords = records.value?.[line]

    return {
      type: 'line' as const,
      name: t(`banking_statistics.series.${line}`),
      showSymbol: false,
      color,
      lineStyle: { width: 2, color },
      data: points.value.map((point) => point[LINE_FIELD[line]]),
      markPoint: lineRecords
        ? {
            data: [
              recordMark(lineRecords.max, 'max', color),
              recordMark(lineRecords.min, 'min', color)
            ]
          }
        : undefined
    }
  })
}))
</script>

<template>
  <WidgetBankingStatsBlock id="currency" :title-key="title.key" :title-params="title.params" :as-of="asOf">
    <p class="text-muted-foreground mb-3 text-sm">{{ t('banking_statistics.currency.subtitle') }}</p>
    <LazyVChart v-if="isChartReady" :option="chartOption" autoresize class="h-72 w-full sm:h-80" />
    <div v-else class="h-72 w-full animate-pulse rounded-lg bg-gray-100 sm:h-80" />
  </WidgetBankingStatsBlock>
</template>
